// KST 고정 시각·날짜 헬퍼 (마스터 2026-10-03 «utc되어있는거 모두 kst로 바꿔»).
//
// 백엔드는 UTC naive('Z' 없는 ISO)로 저장하고 컨테이너도 UTC 다 → 서버가 준 문자열을
// 그대로 자르면 화면에 **UTC 가 찍힌다**(ABC «최종 분석 시각»이 엑셀과 9시간 차였다).
// 반대로 `new Date().toISOString()` 으로 '오늘'을 만들면 KST 00~09시에 하루 어긋난다.
//
// 🔴 브라우저·컨테이너 시간대와 무관하게 늘 KST 로 읽고 쓴다(`timeZone: 'Asia/Seoul'` 고정).
// 🔴 `hour12: false` 는 쓰지 않는다 — ICU 가 KST 자정 직후를 '24:05' 로 낸다. `hourCycle: 'h23'`.
import { kstDateKey } from './msgDate';

const KST = 'Asia/Seoul';

// 날짜만 있는 값('2026-10-02'). 🔴 끝에 'Z' 만 붙이면('2026-10-02Z') 규격 밖이라 해석이 엔진에 달린다
//    → UTC 자정으로 못 박는다(날짜 전용 컬럼 cost_date 등이 조용히 빈 값/전날로 흐르지 않게).
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

// en-CA = YYYY-MM-DD 형식. formatToParts 로 조립해 로캘 구분자에 기대지 않는다.
const KST_PARTS = new Intl.DateTimeFormat('en-CA', {
  timeZone: KST,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

// 서버의 'Z' 없는 ISO 는 UTC 로 읽는다(오프셋이 붙어 있으면 그대로). 해석 불가면 null.
export function parseServerUtc(value) {
  if (!value) return null;
  const text = String(value);
  const iso = DATE_ONLY.test(text)
    ? `${text}T00:00:00Z`
    : (/Z$|[+-]\d\d:?\d\d$/.test(text) ? text : `${text}Z`);
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

const parts = (value) => {
  const d = parseServerUtc(value);
  if (!d) return null;
  const out = {};
  KST_PARTS.formatToParts(d).forEach(({ type, value: part }) => { out[type] = part; });
  return out.year && out.month && out.day && out.hour && out.minute ? out : null;
};

// 'YYYY-MM-DD HH:mm'(KST). 해석 불가면 '' — 호출부가 자기 빈 값 표시('—'·'-')를 쓴다.
export function fmtKstDateTime(value) {
  const p = parts(value);
  return p ? `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}` : '';
}

// 'MM-DD HH:mm'(KST). 목록처럼 좁은 칸에서 연도를 떼고 쓴다.
export function fmtKstShort(value) {
  const p = parts(value);
  return p ? `${p.month}-${p.day} ${p.hour}:${p.minute}` : '';
}

// 비교용 KST 날짜('YYYY-MM-DD'). 🔴 해석 불가면 '' 다(null 아님) — 날짜 필터가
// «created_at 없는 행은 시작일 필터에서 제외» 동작을 그대로 유지해야 한다.
export function kstDateOf(value) {
  if (!value) return '';
  const text = String(value);
  // 날짜만 있는 값은 시간대 변환 대상이 아니다 — 그 날짜가 그대로 답이다(값이 깨졌으면 '').
  if (DATE_ONLY.test(text)) return parseServerUtc(text) ? text : '';
  return kstDateKey(text) || '';
}

// KST 오늘('YYYY-MM-DD'). 기본 기간·파일명 날짜는 전부 이걸 쓴다.
export function kstToday() {
  return kstDateOf(new Date().toISOString());
}

// KST 오늘 − n 일('YYYY-MM-DD'). 날짜 산술은 UTC 자정 기준으로 해 로컬 시간대에 안 흔들린다.
export function kstDaysAgo(days) {
  const today = kstToday();
  if (!today) return '';
  // 🔴 숫자가 아닌 일수는 0 으로 떨어뜨린다 — NaN 이면 Date 가 Invalid 가 되고
  //    `toISOString()` 이 RangeError 를 던져, 렌더 중 호출부(기본 기간·버튼)의 화면이 통째로 빈다.
  const asNumber = Number(days);
  const offset = Number.isFinite(asNumber) ? Math.trunc(asNumber) : 0;
  const [year, month, day] = today.split('-').map(Number);
  const base = new Date(Date.UTC(year, month - 1, day));
  base.setUTCDate(base.getUTCDate() - offset);
  return base.toISOString().slice(0, 10);
}
